import { Box, Flex, StyleProps } from '@chakra-ui/react';
import { Children, cloneElement, FC, isValidElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@bonusly/ds-tooltip';
import { rem } from '@bonusly/ds-utils';

import { AvatarSize } from '../types';
import { AvatarProps } from './avatar';

export type AvatarGroupProps = StyleProps & {
	children?: ReactNode;
	max?: number;
	size?: AvatarProps['size'];
};

// Deliberately unchanged: this is shared across ~20 surfaces, so tightening the
// overlap here is its own change. The overflow count reads as part of the pile
// because of the stack-item wrapper and z-index below, not the offset.
const OffsetSizeMap: Record<AvatarSize, string> = {
	sm: rem(-4),
	md: rem(-8),
	lg: rem(-12),
	xl: rem(-16),
	'2xl': rem(-20),
};

const SizeStyleMap: Record<AvatarSize, { boxSize: string; fontSize: string }> = {
	sm: { boxSize: rem(32), fontSize: 'sm' },
	md: { boxSize: rem(40), fontSize: 'md' },
	lg: { boxSize: rem(48), fontSize: 'md' },
	xl: { boxSize: rem(56), fontSize: 'lg' },
	'2xl': { boxSize: rem(72), fontSize: 'xl' },
};

export const AvatarGroup: FC<AvatarGroupProps> = ({ children, max = 5, size = 'md', ...rest }) => {
	const { t } = useTranslation('common');
	const allAvatars = Children.toArray(children);
	const avatars = allAvatars.slice(0, max);
	const remainingNames = allAvatars
		.slice(max)
		.map<string>((avatar) => {
			if (!isValidElement(avatar)) {
				return null;
			}

			return avatar.props.name;
		})
		.sort((a, b) => a.localeCompare(b));
	const displayNames = remainingNames.slice(0, 10);
	const extra = remainingNames.length - displayNames.length;
	const displayTooltip = displayNames.filter(Boolean).length > 0;
	const sizeStyle = SizeStyleMap[size];

	return (
		<Flex alignItems="center" {...rest}>
			{avatars.map((avatar, index) => {
				if (!isValidElement(avatar)) {
					return null;
				}

				// Wrap each child so z-index applies to the stack item. Avatar's own
				// zIndex prop lands on the inner circle inside `isolation: isolate`,
				// which cannot reorder siblings in the facepile.
				return (
					<Box
						key={avatar.key ?? index}
						display="inline-flex"
						flexShrink={0}
						position="relative"
						zIndex={index + 1}
						marginInlineStart={index === 0 ? undefined : OffsetSizeMap[size]}
						data-avatar-group-item=""
					>
						{cloneElement<any>(avatar as any, { size })}
					</Box>
				);
			})}

			{remainingNames.length > 0 && (
				<Tooltip
					isDisabled={!displayTooltip}
					openDelay={500}
					label={t('intl_overflow_list', {
						names: displayNames,
						count: extra,
					})}
				>
					<Box
						as="span"
						alignItems="center"
						backgroundColor="bg.subtle"
						borderRadius="full"
						color="text.default"
						display="inline-flex"
						flexShrink={0}
						fontWeight="medium"
						justifyContent="center"
						lineHeight={1}
						marginInlineStart={OffsetSizeMap[size]}
						position="relative"
						zIndex={avatars.length + 1}
						transitionDuration="normal"
						transitionProperty="common"
						data-testid="avatar-group-overflow"
						sx={{
							outlineWidth: '1px',
							outlineColor: 'bg.surface',
							outlineStyle: 'solid',
						}}
						{...sizeStyle}
						{...(displayTooltip && {
							_hover: {
								bgColor: 'secondary.bg',
								color: 'secondary.fgOnSurface',
								cursor: 'default',
							},
						})}
					>
						+{remainingNames.length}
					</Box>
				</Tooltip>
			)}
		</Flex>
	);
};
